/**
 * Custom hook for managing automatic token refresh
 */

import { useEffect, useRef } from 'react';
import { isTokenExpiringSoon } from '../utils/tokenUtils';
import { refreshTokenService } from '../services/refreshTokenService';

export interface UseTokenRefreshProps {
  user: { access_token: string } | null;
  refreshAuth: () => Promise<void>;
}

export const useTokenRefresh = ({ user, refreshAuth }: UseTokenRefreshProps) => {
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Set up automatic refresh interval
  useEffect(() => {
    // Clear any existing interval immediately
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    if (!user) {
      return;
    }

    const setupRefreshInterval = () => {
      // Clear existing interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }

      // Set up new interval (check every hour)
      refreshIntervalRef.current = setInterval(async () => {
        // Double-check user still exists before attempting refresh
        if (!user) {
          if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
          }
          return;
        }

        if (user && isTokenExpiringSoon(user.access_token, 5)) {
          // Check if we're in cooldown period to avoid infinite loops
          const retryState = refreshTokenService.getRetryState();
          if (retryState.refreshRetryCount < 3 || (Date.now() - retryState.lastRefreshAttempt) > 5 * 60 * 1000) {
            try {
              await refreshAuth();
            } catch (error) {
              // Don't retry immediately, let the cooldown mechanism handle it
            }
          }
        }
      }, 60 * 60 * 1000); // 1 hour
    };

    setupRefreshInterval();

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [user, refreshAuth]);

  // Handle visibility change (like IM Partner Dashboard)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      // Don't attempt refresh if no user
      if (!user) {
        return;
      }

      if (document.visibilityState === 'visible' && user) {
        // Check if token needs refresh when user returns to tab
        if (isTokenExpiringSoon(user.access_token, 5)) {
          // Only attempt refresh if not in cooldown period
          const retryState = refreshTokenService.getRetryState();
          if (retryState.refreshRetryCount < 3 || (Date.now() - retryState.lastRefreshAttempt) > 5 * 60 * 1000) {
            try {
              await refreshAuth();
            } catch (error) {
              // Don't retry immediately, let the cooldown mechanism handle it
            }
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, refreshAuth]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);
};
